import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:6555');
const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.zip,.7z,.psd,.ai,.cdr,.mp4,.mov';

function fileName(file) {
  if (!file) return 'file';
  if (typeof file === 'string') return file.split('/').pop();
  return file.name || file.filename || 'file';
}
function fileType(file) {
  return typeof file === 'object' ? (file.content_type || '') : '';
}

function isPreviewable(file) {
  const name = fileName(file);
  const type = fileType(file);
  return /^image\//.test(type) || type === 'application/pdf' || /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(name);
}


const BLOCK_NAMES = {
  tech: '🔧 Тех. работы', design: '🎨 Дизайн', video: '🎬 Видео',
  loyalty: '🎁 Лояльность', social: '📱 Контент',
};
const STATUS_LABELS = { new: 'Новая', in_progress: 'В работе', closed: 'Закрыта' };
const PRIORITY_LABEL = { urgent: '🔴 Срочно', normal: '🟡 Обычно', low: '🟢 Не горит' };

function ticketPoints(ticket) {
  if (Array.isArray(ticket?.points) && ticket.points.length) return ticket.points;
  return ticket?.point ? [ticket.point] : [];
}

function ticketAccent(network) {
  if (network === 'hookah') return 'ticket-accent-hookah';
  if (network?.startsWith('tabakov')) return 'ticket-accent-tabakov';
  return '';
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msgText, setMsgText] = useState('');
  const [msgFiles, setMsgFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const messagesEndRef = useRef();

  const load = async () => {
    try {
      const res = await api.get(`/api/admin/tickets/${id}`);
      setTicket(res.data);
    } catch {
      setError('Заявка не найдена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [ticket?.messages]);

  const closePreview = () => {
    if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
    setPreview(null);
  };

  const openFile = async (file) => {
    if (typeof file === 'string') {
      const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!isPreviewable(file)) {
      const res = await api.get(`/api/files/${file.id}/link`);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
      return;
    }

    const res = await api.get(`/api/files/${file.id}/inline`, { responseType: 'blob' });
    const objectUrl = URL.createObjectURL(res.data);
    setPreview({
      objectUrl,
      name: fileName(file),
      type: file.content_type || res.data.type || '',
    });
  };

  const deleteTicket = async () => {
    if (!window.confirm(`Удалить заявку ${ticket.ticket_number}? Это действие необратимо.`)) return;
    try {
      await api.delete(`/api/admin/tickets/${id}`);
      navigate('/admin/tickets');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const changeStatus = async (status) => {
    try {
      const res = await api.put(`/api/admin/tickets/${id}/status`, { status });
      setTicket(prev => ({ ...prev, status: res.data.status, change_history: res.data.change_history }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка смены статуса');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('ticket_id', id);
      form.append('direction', 'outgoing');
      const res = await api.post('/api/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsgFiles(prev => [...prev, res.data]);
    } catch {
      setError('Ошибка загрузки файла');
    } finally {
      setUploading(false);
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if (!msgText.trim() && msgFiles.length === 0) return;
    setSending(true);
    try {
      const res = await api.post(`/api/admin/tickets/${id}/message`, { content: msgText, files: msgFiles });
      setTicket(prev => ({ ...prev, messages: [...(prev.messages || []), res.data] }));
      setMsgText('');
      setMsgFiles([]);
    } catch {
      setError('Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="page-loader">Загрузка...</div>;
  if (error && !ticket) return <div className="error-page"><div className="error-msg">{error}</div><button className="btn-secondary" onClick={() => navigate(-1)}>← Назад</button></div>;

  const points = ticketPoints(ticket);
  const accentClass = ticketAccent(ticket.network);

  return (
    <div className="page ticket-detail">
      {preview && <FilePreviewModal preview={preview} onClose={closePreview} />}
      <div className="page-header">
        <button className="btn-ghost" onClick={() => navigate('/admin/tickets')}>← Назад</button>
        <h1>{ticket.ticket_number}</h1>
        <span className={`status-badge status--${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div className="ticket-layout">
        <div className="ticket-main">
          <div className={`info-card ticket-network-accent ${accentClass}`}>
            <div className="info-row"><span>Категория</span><span>{BLOCK_NAMES[ticket.block] || ticket.block}</span></div>
            <div className="info-row"><span>Тип</span><span>{ticket.problem_type}</span></div>
            <div className="info-row info-row-points">
              <span>{points.length > 1 ? 'Точки' : 'Точка'}</span>
              <span className="ticket-points-list">
                {points.length ? points.map((point, idx) => <span key={`${point}-${idx}`} className="ticket-point-chip">{point}</span>) : '—'}
              </span>
            </div>
            <div className="info-row"><span>Приоритет</span><span>{PRIORITY_LABEL[ticket.priority]}</span></div>
            {ticket.deadline && <div className="info-row"><span>Дедлайн</span><span>{new Date(ticket.deadline).toLocaleString('ru-RU')}</span></div>}
            <div className="info-row"><span>Создана</span><span>{new Date(ticket.created_at).toLocaleString('ru-RU')}</span></div>
            <div className="info-row"><span>Автор</span><span>{ticket.user?.full_name} ({ticket.user?.role_name})</span></div>
          </div>

          <div className="description-card">
            <h3>Описание</h3>
            <p>{ticket.description}</p>
          </div>

          {ticket.equipment?.length > 0 && (
            <div className="equipment-card">
              <h3>Связанное оборудование</h3>
              <div className="ticket-equipment-list">
                {ticket.equipment.map(item => (
                  <button key={item.id} type="button" className="ticket-equipment-item" onClick={() => navigate(`/admin/inventory?asset=${item.id}`)}>
                    <span className={`ticket-equipment-status status-${item.status || 'active'}`} />
                    <span>
                      <strong>{item.name}</strong>
                      <small>{[item.point, item.inventory_number].filter(Boolean).join(' · ') || 'Открыть паспорт устройства'}</small>
                    </span>
                    <b>›</b>
                  </button>
                ))}
              </div>
            </div>
          )}

          {ticket.files?.length > 0 && (
            <div className="files-card">
              <h3>Файлы</h3>
              <div className="files-list">
                {ticket.files.map((f, i) => <SecureFilePreview key={i} file={f} onOpen={openFile} />)}
              </div>
            </div>
          )}

          <div className="messages-section">
            <h3>Сообщения</h3>
            <div className="messages-list">
              {(ticket.messages || []).length === 0 ? (
                <div className="empty-state small">Сообщений пока нет</div>
              ) : (
                ticket.messages.map(m => (
                  <div key={m.id} className={`message-bubble ${m.user?.id !== ticket.user?.id ? 'message-admin' : 'message-user'}`}>
                    <div className="message-author">{m.user?.full_name}</div>
                    <div className="message-content">{m.content}</div>
                    {m.files?.map((f, i) => <SecureFilePreview key={i} file={f} onOpen={openFile} />)}
                    <div className="message-time">{new Date(m.created_at).toLocaleString('ru-RU')}</div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-area">
              <textarea
                placeholder="Написать сообщение..."
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                rows={3}
                onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') sendMessage(); }}
              />
              {msgFiles.length > 0 && (
                <div className="attached-files">
                  {msgFiles.map((f, i) => (
                    <span key={i} className="attached-file">
                      📎 {fileName(f)}
                      <button onClick={() => setMsgFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="message-actions">
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept={FILE_ACCEPT} />
                <button className="btn-ghost" onClick={() => fileInputRef.current.click()} disabled={uploading}>
                  {uploading ? 'Загрузка...' : '📎 Файл'}
                </button>
                <button className="btn-primary" onClick={sendMessage} disabled={sending || (!msgText.trim() && msgFiles.length === 0)}>
                  {sending ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="ticket-sidebar">
          <div className="actions-card">
            <h3>Действия</h3>
            {ticket.status === 'new' && (
              <button className="btn-primary full-width" onClick={() => changeStatus('in_progress')}>🔨 Взять в работу</button>
            )}
            {ticket.status === 'in_progress' && (
              <button className="btn-success full-width" onClick={() => changeStatus('closed')}>✅ Закрыть заявку</button>
            )}
            {ticket.status === 'closed' && (
              <button className="btn-secondary full-width" onClick={() => changeStatus('new')}>🔄 Переоткрыть</button>
            )}
            <button className="btn-danger full-width" style={{ marginTop: 12 }} onClick={deleteTicket}>🗑 Удалить заявку</button>
          </div>

          {ticket.change_history?.length > 0 && (
            <div className="history-card">
              <h3>История изменений</h3>
              {[...ticket.change_history].reverse().map((h, i) => (
                <div key={i} className="history-item">
                  <span className="history-time">{new Date(h.timestamp).toLocaleString('ru-RU')}</span>
                  <span>{h.old ? `${h.old} → ${h.new}` : `Создана`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilePreview({ file }) {
  const openFile = async () => {
    if (typeof file === 'string') {
      const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const res = await api.get(`/api/files/${file.id}/link`);
    window.open(res.data.url, '_blank', 'noopener,noreferrer');
  };

  const name = fileName(file);
  const isLegacyImage = typeof file === 'string' && /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
  if (isLegacyImage) {
    const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
    return <a href={fullUrl} target="_blank" rel="noreferrer"><img src={fullUrl} alt="attachment" className="file-preview-img" /></a>;
  }
  return <button type="button" onClick={openFile} className="file-link">📎 {name}</button>;
}


function SecureFilePreview({ file, onOpen }) {
  const name = fileName(file);
  const isLegacyImage = typeof file === 'string' && /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
  if (isLegacyImage) {
    const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
    return <a href={fullUrl} target="_blank" rel="noreferrer"><img src={fullUrl} alt="" className="file-thumb" /></a>;
  }

  const icon = isPreviewable(file) ? '??' : '??';
  return <button type="button" onClick={() => onOpen(file)} className="file-link">{icon} {name}</button>;
}

function FilePreviewModal({ preview, onClose }) {
  const isImage = /^image\//.test(preview.type) || /\.(jpg|jpeg|png|gif|webp)$/i.test(preview.name);
  const isPdf = preview.type === 'application/pdf' || /\.pdf$/i.test(preview.name);

  return (
    <div className="file-modal-backdrop" onClick={onClose}>
      <div className="file-modal" onClick={e => e.stopPropagation()}>
        <div className="file-modal-header">
          <span>{preview.name}</span>
          <button type="button" onClick={onClose}>?</button>
        </div>
        <div className="file-modal-body">
          {isImage && <img src={preview.objectUrl} alt={preview.name} />}
          {isPdf && <iframe src={preview.objectUrl} title={preview.name} />}
          {!isImage && !isPdf && <a href={preview.objectUrl} target="_blank" rel="noreferrer">Open file</a>}
        </div>
      </div>
    </div>
  );
}
